import { Formula, FormulaPoint, FormulaExample, FormulaType } from "@/types/formula";
import { Point } from "@/types/shapes";
import { create, all } from 'mathjs';

// Constants
// Increase sampling caps to reduce visual discontinuities in plots
const MAX_SAMPLES = 20000;
const MIN_SAMPLES = 200;
const DEFAULT_X_RANGE: [number, number] = [-10, 10];
const DEFAULT_T_RANGE: [number, number] = [0, 2 * Math.PI];

// Utility functions
const clampSamples = (samples: number): number => 
  Math.min(Math.max(samples, MIN_SAMPLES), MAX_SAMPLES);

const getCanvasDimensions = (): { width: number; height: number } => ({
  width: window.innerWidth,
  height: window.innerHeight
});

const calculateVisibleXRange = (
  gridPosition: Point,
  pixelsPerUnit: number
): [number, number] => {
  const { width } = getCanvasDimensions();
  const xMin = (0 - gridPosition.x) / pixelsPerUnit;
  const xMax = (width - gridPosition.x) / pixelsPerUnit;
  return [xMin, xMax];
};

// Math.js configuration
const math = create(all, { matrix: 'Array', number: 'number', predictable: true });
const SAFE_SCOPE_BASE: Record<string, unknown> = {
  sin: math.sin,
  cos: math.cos,
  tan: math.tan,
  sec: (math as any).sec || ((v: number) => 1 / Math.cos(v)),
  csc: (math as any).csc || ((v: number) => 1 / Math.sin(v)),
  cot: (math as any).cot || ((v: number) => 1 / Math.tan(v)),
  sqrt: math.sqrt,
  abs: math.abs,
  log: math.log,
  exp: math.exp,
  pow: math.pow,
  PI: Math.PI,
  E: Math.E,
  deg: (v: number) => v * Math.PI / 180,
};

const sanitizeExpression = (expr: string): string => {
  if (!expr) return '';
  const normalized = expr
    .replace(/\*\*/g, '^')
    .replace(/π/g, 'PI')
    .replace(/ln\s*\(/g, 'log(');

  const sanitized = normalized.trim()
    .replace(/Math\.(sin|cos|tan|sqrt|abs|log|exp|pow|PI|E)\b/g, '$1')
    // Allow Unicode letters in identifiers
    .replace(/[^\p{L}0-9_\s+\-*/%^().,;^]/gu, '');
  return sanitized;
};

// Replace any remaining single-letter tokens with a default numeric value to avoid "Undefined symbol" errors
// Skips the independent symbol so it remains as the variable.
// Precompiled single-letter token regex; reset lastIndex before each replace when using the global flag
const RE_SINGLE_LETTER_TOKEN = /(^|[^\p{L}0-9_])([\p{L}])($|[^\p{L}0-9_])/gu;
const replaceRemainingSingles = (expr: string, independentSymbol: string): string => {
  if (!expr) return expr;
  RE_SINGLE_LETTER_TOKEN.lastIndex = 0;
  return expr.replace(RE_SINGLE_LETTER_TOKEN, (match, p1, letter, p3) => {
    if (letter === independentSymbol) return match;
    return `${p1}1${p3}`;
  });
};

// Insert implicit multiplication between the independent symbol and single-letter params
// e.g., 'xa' or 'ax' -> 'x*a' (and similarly for 't' in parametric)
const insertImplicitMultiplication = (
  expr: string,
  variables: Formula['variables'] | undefined,
  independentSymbols: string[]
): string => {
  if (independentSymbols.length === 0) return expr;
  // If variables are not provided, fall back to all single-letter candidates (A-Z, a-z)
  const fallbackLetters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const varNames = variables
    ? Object.keys(variables).filter((n) => n.length === 1)
    : fallbackLetters;

  let result = expr;
  for (const sym of independentSymbols) {
    // Generic adjacency: xA -> x*A and Ax -> A*x (single-letter only)
    result = result.replace(
      new RegExp(`(^|[^\\p{L}0-9_])${sym}([\\p{L}])(?![\\p{L}])`, 'gu'),
      (_m, p1, letter) => `${p1}${sym}*${letter}`
    );
    result = result.replace(
      new RegExp(`(^|[^\\p{L}0-9_])([\\p{L}])(?![\\p{L}])${sym}`, 'gu'),
      (_m, p1, letter) => `${p1}${letter}*${sym}`
    );

    // Digit adjacency: 2x -> 2*x and x2 -> x*2
    result = result
      .replace(new RegExp(`(\\d)${sym}\\b`, 'g'), `$1*${sym}`)
      .replace(new RegExp(`\\b${sym}(\\d)`, 'g'), `${sym}*$1`);

    // Parenthesis-parenthesis: )( -> )*(
    result = result.replace(/\)\s*\(/g, ')*(');

    // Number-parenthesis: 2( -> 2*(
    result = result.replace(/(\d)\s*\(/g, '$1*(');

    // Constant-variable and variable-constant for pi/PI and E: PIa -> PI*a, aPI -> a*PI
    result = result.replace(/\b(PI|pi|E)\s*([\p{L}])(?![\p{L}])/gu, (_m, c, v) => `${c}*${v}`);
    result = result.replace(/(^|[^\p{L}0-9_])([\p{L}])(?![\p{L}])\s*(PI|pi|E)\b/gu, (_m, p1, v, c) => `${p1}${v}*${c}`);

    for (const name of varNames) {
      if (name === sym) continue;
      // Left or right adjacency without an operator
      const re1 = new RegExp(`(^|[^\\p{L}0-9_])${sym}${name}($|[^\\p{L}0-9_])`, 'gu');
      result = result.replace(re1, (_m, p1, p2) => `${p1}${sym}*${name}${p2}`);
      const re2 = new RegExp(`(^|[^\\p{L}0-9_])${name}${sym}($|[^\\p{L}0-9_])`, 'gu');
      result = result.replace(re2, (_m, p1, p2) => `${p1}${name}*${sym}${p2}`);
    }
  }
  // Final global pass for constant-variable adjacency independent of sym
  result = result.replace(/\b(PI|pi|E)\s*([\p{L}])(?![\p{L}])/gu, (_m, c, v) => `${c}*${v}`);
  result = result.replace(/(^|[^\p{L}0-9_])([\p{L}])(?![\p{L}])\s*(PI|pi|E)\b/gu, (_m, p1, v, c) => `${p1}${v}*${c}`);
  // Ensure single-letter variable immediately followed by constant also gets a star: aPI -> a*PI
  result = result.replace(/(^|[^\p{L}0-9_])([\p{L}])(?=\s*(PI|pi|E)\b)/gu, (_m, p1, v) => `${p1}${v}*`);
  return result;
};

// Substitute single-letter variables with their numeric values, skipping the independent symbol
const substituteVariables = (
  expr: string,
  variables: Formula['variables'] | undefined,
  independentSymbol: string
): string => {
  if (!variables) return expr;
  let out = expr;
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  for (const [name, cfg] of Object.entries(variables)) {
    if (name === independentSymbol) continue;
    if (name.length !== 1) continue;
    const pattern = `(?<![\\p{L}\\p{N}_])${escapeRegex(name)}(?![\\p{L}\\p{N}_])`;
    const re = new RegExp(pattern, 'gu');
    out = out.replace(re, String(cfg.value));
  }
  return out;
};

// Build an evaluable expression by inserting implicit multiplications and substituting variables
export const buildEvaluableExpression = (
  expression: string,
  variables: Formula['variables'] | undefined,
  independentSymbol: 'x' | 't' = 'x'
): string => {
  const withImplicit = insertImplicitMultiplication(expression, variables, [independentSymbol]);
  const substituted = substituteVariables(withImplicit, variables, independentSymbol);
  // Replace any remaining single-letter tokens except the independent symbol
  return replaceRemainingSingles(substituted, independentSymbol);
};

export const createFunctionFromExpression = (
  expression: string,
  scaleFactor: number,
  independentVar: string = 'x'
): ((x: number) => number) => {
  const sanitized = sanitizeExpression(expression);
  if (!sanitized) return () => NaN;

  try {
    const compiled = math.compile(sanitized);
    return (x: number) => {
      try {
        // Use the configurable independent variable in the scope
        const scope = { ...SAFE_SCOPE_BASE, [independentVar]: x };
        const result = compiled.evaluate(scope);
        const num = Number(result);
        return isFinite(num) ? num * scaleFactor : NaN;
      } catch {
        return NaN;
      }
    };
  } catch {
    return () => NaN;
  }
};

const toCanvasCoordinates = (
  x: number,
  y: number,
  gridPosition: Point,
  pixelsPerUnit: number
): { x: number; y: number } => ({
  x: gridPosition.x + x * pixelsPerUnit,
  y: gridPosition.y - y * pixelsPerUnit
});

// Function type detection
interface FunctionCharacteristics {
  isLogarithmic: boolean;
  isTangent: boolean;
  hasSingularity: boolean;
}

const detectFunctionCharacteristics = (expression: string): FunctionCharacteristics => {
  const normalized = expression.replace(/\s+/g, '');
  return {
    isLogarithmic: /log|ln/.test(normalized),
    isTangent: normalized.includes('tan('),
    hasSingularity: /1\/x|1\/\(|tan\(/.test(normalized)
  };
};

// X-value generation
const generateXValues = (
  range: [number, number],
  samples: number,
  chars: FunctionCharacteristics
): number[] => {
  const [min, max] = range;
  const step = (max - min) / samples;
  const values: number[] = [];
  
  if (chars.isLogarithmic && min < 0 && max > 0) {
    // For log-like functions, we want dense sampling near 0 on both sides,
    // but never include 0 itself. Use exponential spacing from a small epsilon.
    const posSamples = Math.floor(samples * 0.5);
    const epsilon = 1e-6; // avoid 0 while still sampling very close to it
    const maxPos = Math.max(max, epsilon * 2);
    const maxNegMag = Math.max(-min, epsilon * 2);

    // Positive side: [epsilon, max]
    for (let i = 0; i <= posSamples; i++) {
      const t = i / posSamples; // 0..1
      const x = epsilon * Math.pow(maxPos / epsilon, t);
      if (x > epsilon) values.push(x);
    }
    // Negative side: [-epsilon, min]
    for (let i = 0; i <= posSamples; i++) {
      const t = i / posSamples; // 0..1
      const xn = -epsilon * Math.pow(maxNegMag / epsilon, t);
      if (xn < -epsilon) values.push(xn);
    }
    return values.sort((a, b) => a - b);
  }

  if (chars.hasSingularity && min < 0 && max > 0) {
    const epsilon = 1e-8;
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const x = min + t * (max - min);
      if (Math.abs(x) > epsilon) values.push(x);
    }
    return values;
  }

  return Array.from({ length: samples + 1 }, (_, i) => min + i * step);
};

// Core evaluation
const evaluatePoints = (
  formula: Formula,
  gridPosition: Point,
  pixelsPerUnit: number,
  xValues: number[],
  fn: (x: number) => number
): FormulaPoint[] => {
  const points: FormulaPoint[] = [];
  const chars = detectFunctionCharacteristics(formula.expression);
  
  for (const x of xValues) {
    let y = NaN;
    try {
      // Never attempt to evaluate exactly at 0 for log-like expressions
      if (chars.isLogarithmic && Math.abs(x) < 1e-10) {
        y = NaN;
        } else {
          y = fn(x);
      }
      // Discard extreme values that would blow the path
      y = Math.abs(y) > 1e6 ? NaN : y;
    } catch (_e) { /* noop */ }

    const isValid = !isNaN(y) && isFinite(y);
    const coords = toCanvasCoordinates(x, isValid ? y : 0, gridPosition, pixelsPerUnit);
    points.push({ ...coords, isValid });
  }
  
  return points;
};

// Validation
export const validateFormula = (formula: Formula): { isValid: boolean; error?: string } => {
  try {
    if (!formula.expression) return { isValid: false, error: 'Empty expression' };

    if (formula.type === 'parametric') {
      const withImplicit = insertImplicitMultiplication(formula.expression, formula.variables, ['t']);
      const [xRaw, yRaw] = withImplicit.split(';').map(e => e.trim());
      const xExpr = replaceRemainingSingles(substituteVariables(xRaw || '', formula.variables, 't'), 't');
      const yExpr = replaceRemainingSingles(substituteVariables(yRaw || '', formula.variables, 't'), 't');
      if (!xExpr || !yExpr) return { isValid: false, error: 'Invalid parametric format' };
      
      const sx = sanitizeExpression(xExpr);
      const sy = sanitizeExpression(yExpr);
      if (!sx || !sy) return { isValid: false, error: 'Invalid characters' };
      
      const cx = math.compile(sx);
      const cy = math.compile(sy);
      cx.evaluate({ ...SAFE_SCOPE_BASE, t: 0 });
      cy.evaluate({ ...SAFE_SCOPE_BASE, t: 0 });
    } else {
      const withImplicit = insertImplicitMultiplication(formula.expression, formula.variables, ['x']);
      const substituted = replaceRemainingSingles(substituteVariables(withImplicit, formula.variables, 'x'), 'x');
      const sx = sanitizeExpression(substituted);
      if (!sx) return { isValid: false, error: 'Invalid characters' };
      math.compile(sx).evaluate({ ...SAFE_SCOPE_BASE, x: 0 });
    }
    
    return { isValid: true };
  } catch (e) {
    return { isValid: false, error: `Invalid expression: ${(e as Error).message}` };
  }
};

// Main evaluation functions
export const evaluateFunction = (
  formula: Formula,
  gridPosition: Point,
  pixelsPerUnit: number,
  overrideSamples?: number
): FormulaPoint[] => {
  const validation = validateFormula(formula);
  if (!validation.isValid) {
    console.error(validation.error);
    return [{ x: 0, y: 0, isValid: false }];
  }
  
  // Determine visible range and derive a suggested samples count based on screen pixels
  const xRange = calculateVisibleXRange(gridPosition, pixelsPerUnit);
  const visibleWidthUnits = Math.abs(xRange[1] - xRange[0]);
  const visibleWidthPixels = Math.max(1, Math.floor(visibleWidthUnits * pixelsPerUnit));
  // Target high density: ~1.5 samples per pixel (clamped later)
  const suggestedSamples = Math.floor(visibleWidthPixels * 1.5);
  const samples = clampSamples(
    Math.max(
      overrideSamples ?? 0,
      formula.samples || 0,
      suggestedSamples
    )
  );
  const independentVar = formula.independentVar || 'x';
  const withImplicit = insertImplicitMultiplication(formula.expression, formula.variables, [independentVar]);
  const substituted = replaceRemainingSingles(substituteVariables(withImplicit, formula.variables, independentVar), independentVar);
  const chars = detectFunctionCharacteristics(substituted);
  const xValues = generateXValues(xRange, samples, chars);
  const fn = createFunctionFromExpression(substituted, formula.scaleFactor, independentVar);

  return evaluatePoints(formula, gridPosition, pixelsPerUnit, xValues, fn);
};

export const evaluateParametric = (
  formula: Formula,
  gridPosition: Point,
  pixelsPerUnit: number
): FormulaPoint[] => {
  if (!formula.tRange) return [];
  
  const validation = validateFormula(formula);
  if (!validation.isValid) {
    console.error(validation.error);
    return [{ x: 0, y: 0, isValid: false }];
  }
  
  const samples = clampSamples(formula.samples);
  const step = (formula.tRange[1] - formula.tRange[0]) / samples;
  const withImplicit = insertImplicitMultiplication(formula.expression, formula.variables, ['t']);
  const [xRaw, yRaw] = withImplicit.split(';').map(e => e.trim());
  const xExpr = replaceRemainingSingles(substituteVariables(xRaw || '', formula.variables, 't'), 't');
  const yExpr = replaceRemainingSingles(substituteVariables(yRaw || '', formula.variables, 't'), 't');
  const cx = math.compile(sanitizeExpression(xExpr));
  const cy = math.compile(sanitizeExpression(yExpr));
    
    const points: FormulaPoint[] = [];
    for (let i = 0; i <= samples; i++) {
      const t = formula.tRange[0] + i * step;
    const x = Number(cx.evaluate({ ...SAFE_SCOPE_BASE, t }));
    const y = Number(cy.evaluate({ ...SAFE_SCOPE_BASE, t })) * formula.scaleFactor;
    const isValid = isFinite(x) && isFinite(y);
      const coords = toCanvasCoordinates(x, y, gridPosition, pixelsPerUnit);
      points.push({ ...coords, isValid });
    }
    
    return points;
};

export const evaluatePolar = (
  formula: Formula,
  gridPosition: Point,
  pixelsPerUnit: number
): FormulaPoint[] => {
  if (!formula.tRange) return [];
  
  const validation = validateFormula(formula);
  if (!validation.isValid) {
    console.error(validation.error);
    return [{ x: 0, y: 0, isValid: false }];
  }
  
  const samples = clampSamples(formula.samples);
  const step = (formula.tRange[1] - formula.tRange[0]) / samples;
  const withImplicit = insertImplicitMultiplication(formula.expression, formula.variables, ['t']);
  const substituted = replaceRemainingSingles(substituteVariables(withImplicit, formula.variables, 't'), 't');
  const fn = createFunctionFromExpression(substituted, formula.scaleFactor);
    
    const points: FormulaPoint[] = [];
    for (let i = 0; i <= samples; i++) {
      const theta = formula.tRange[0] + i * step;
    const r = fn(theta);
    const isValid = isFinite(r);
        const x = r * Math.cos(theta);
        const y = r * Math.sin(theta);
        const coords = toCanvasCoordinates(x, y, gridPosition, pixelsPerUnit);
        points.push({ ...coords, isValid });
    }
    
    return points;
};

export const evaluateFormula = (
  formula: Formula,
  gridPosition: Point,
  pixelsPerUnit: number,
  isDragging: boolean = false
): FormulaPoint[] => {
  switch (formula.type) {
    case 'function':
      return evaluateFunction(formula, gridPosition, pixelsPerUnit, isDragging ? 100 : undefined);
    case 'parametric':
      return evaluateParametric(formula, gridPosition, pixelsPerUnit);
    case 'polar':
      return evaluatePolar(formula, gridPosition, pixelsPerUnit);
    default:
      console.error(`Unknown formula type: ${formula.type}`);
      return [];
  }
};

// Formula examples
export const getFormulaExamples = (): FormulaExample[] => [
  // Basic functions
  {
    name: 'Linear function',
    type: 'function',
    expression: '2*x + 1',
    xRange: DEFAULT_X_RANGE,
    category: 'basic',
    description: 'f(x) = 2x + 1'
  },
  {
    name: 'Quadratic function',
    type: 'function',
    expression: 'x*x',
    xRange: DEFAULT_X_RANGE,
    category: 'basic',
    description: 'f(x) = x²'
  },
  {
    name: 'Cubic function',
    type: 'function',
    expression: 'x*x*x',
    xRange: DEFAULT_X_RANGE,
    category: 'basic',
    description: 'f(x) = x³'
  },
  
  // Trigonometric functions
  {
    name: 'Sine function',
    type: 'function',
    expression: 'sin(x)',
    xRange: DEFAULT_X_RANGE,
    category: 'trigonometric',
    description: 'f(x) = sin(x)'
  },
  {
    name: 'Cosine function',
    type: 'function',
    expression: 'cos(x)',
    xRange: DEFAULT_X_RANGE,
    category: 'trigonometric',
    description: 'f(x) = cos(x)'
  },
  {
    name: 'Tangent function',
    type: 'function',
    expression: 'tan(x)',
    xRange: DEFAULT_X_RANGE,
    category: 'trigonometric',
    description: 'f(x) = tan(x)'
  },
  
  // Exponential and logarithmic functions
  {
    name: 'Exponential function',
    type: 'function',
    expression: 'exp(x)',
    xRange: DEFAULT_X_RANGE,
    category: 'exponential',
    description: 'f(x) = e^x'
  },
  {
    name: 'Natural logarithm',
    type: 'function',
    expression: 'log(abs(x))',
    xRange: DEFAULT_X_RANGE,
    category: 'exponential',
    description: 'f(x) = ln|x|'
  },
  
  // Special functions
  {
    name: 'Absolute value',
    type: 'function',
    expression: 'abs(x)',
    xRange: DEFAULT_X_RANGE,
    category: 'special',
    description: 'f(x) = |x|'
  },
  {
    name: 'Square root',
    type: 'function',
    expression: 'sqrt(abs(x))',
    xRange: DEFAULT_X_RANGE,
    category: 'special',
    description: 'f(x) = √|x|'
  },
  {
    name: 'Sigmoid function',
    type: 'function',
    expression: '1 / (1 + exp(-x))',
    xRange: DEFAULT_X_RANGE,
    category: 'special',
    description: 'f(x) = 1/(1+e^(-x))'
  },
  
  // Polynomial functions
  {
    name: 'Quartic function',
    type: 'function',
    expression: 'x*x*x*x - 3*x*x',
    xRange: DEFAULT_X_RANGE,
    category: 'polynomial',
    description: 'f(x) = x⁴ - 3x²'
  },
  {
    name: 'Quintic function',
    type: 'function',
    expression: 'x*x*x*x*x - 5*x*x*x + 4*x',
    xRange: DEFAULT_X_RANGE,
    category: 'polynomial',
    description: 'f(x) = x⁵ - 5x³ + 4x'
  }
];

export const generateFormulaId = (): string => 
  `formula-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

export const createDefaultFormula = (type: FormulaType = 'function'): Formula => ({
  id: generateFormulaId(),
  type,
  expression: type === 'parametric' ? 'cos(t); sin(t)' : type === 'polar' ? '1' : 'x*x',
  color: `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`,
  strokeWidth: 2,
  xRange: DEFAULT_X_RANGE,
  tRange: type === 'function' ? undefined : DEFAULT_T_RANGE,
  samples: 500,
  scaleFactor: 1.0,
  functionName: 'f',
  independentVar: type === 'parametric' ? 't' : type === 'polar' ? 'θ' : 'x'
});

// Unified LaTeX conversion with comprehensive normalization and advanced mathjs parsing
const normalizeExpressionForParsing = (expression: string): string => {
  if (!expression) return '';
  let s = expression;
  // Remove Math. prefixes and normalize power/const
  s = s.replace(/Math\./g, '');
  s = s.replace(/\*\*/g, '^');
  s = s.replace(/\bPI\b/g, 'pi').replace(/\bE\b/g, 'e');
  // Prefer e^(...) instead of exp(...)
  s = s.replace(/\bexp\s*\(/g, 'e^(');
  // Unicode-aware implicit adjacency around x
  s = s.replace(/(?<![\p{L}0-9_])x([\p{L}])(?![\p{L}0-9_])/gu, 'x*$1');
  s = s.replace(/(?<![\p{L}0-9_])([\p{L}])x(?![\p{L}0-9_])/gu, '$1*x');
  // Implicit numeric adjacency with variables: 2x -> 2*x
  s = s.replace(/(\d+(?:\.\d+)?)\s*([\p{L}])/gu, '$1*$2');
  // Keep 2pi implicit (avoid dot)
  s = s.replace(/(\d+(?:\.\d+)?)\s*\*\s*pi\b/gu, '$1 pi');
  // Paren adjacency: letter( -> letter*(
  s = s.replace(/([\p{L}]+)\s*\(/gu, (_m, name) => {
    const fns = ['sin','cos','tan','asin','acos','atan','atan2','sinh','cosh','tanh','asinh','acosh','atanh','sec','csc','cot','sech','csch','coth','sqrt','abs','log','ln','log10','log2','exp','pow','floor','ceil','round','fix','sign','min','max','mod','gcd','lcm'];
    return fns.includes(name) ? `${name}(` : `${name}*(`;
  });
  // Number next to parentheses
  s = s.replace(/(\d+(?:\.\d+)?)\s*\(/g, '$1*(');
  s = s.replace(/\)\s*(\d+(?:\.\d+)?)/g, ')*$1');
  s = s.replace(/\)\s*\(/g, ')*(');
  // Ensure denominator grouping for patterns like / 2 pi
  s = s.replace(/\/\s*(\d+(?:\.\d+)?)(?:\s*\*\s*|\s*)pi\b/gu, '/($1*pi)');

  // Normalize operator sign sequences to avoid "+ -n" or "- -n" artifacts
  // Collapse repeated plus/minus and simplify numeric negations in parentheses
  const normalizeOps = (str: string): string => {
    let out = str;
    let prev: string;
    do {
      prev = out;
      out = out
        .replace(/\+\s*-\s*/g, ' - ')
        .replace(/-\s*\+\s*/g, ' - ')
        .replace(/-\s*-\s*/g, ' + ')
        .replace(/\+\s*\+\s*/g, ' + ')
        // +(-15) -> - 15, -(-15) -> + 15 (numeric-only to stay safe)
        .replace(/\+\s*\(-\s*([0-9]+(?:\.[0-9]+)?)\s*\)/g, ' - $1')
        .replace(/-\s*\(-\s*([0-9]+(?:\.[0-9]+)?)\s*\)/g, ' + $1');
    } while (out !== prev);
    return out;
  };
  s = normalizeOps(s);
  return s;
};

export const convertToLatex = (expression: string): string => {
  const prepared = normalizeExpressionForParsing(expression);
  if (!prepared) return '';
  try {
    const node = math.parse(prepared);
    let tex: string = node.toTex({ parenthesis: 'auto', implicit: 'hide' } as any);
    // Visual tweaks and formatting improvements
    tex = tex.replace(/\\log(?!_{)/g, '\\ln');
    // Decimal comma for European formatting
    tex = tex.replace(/(?<=\d)\.(?=\d)/g, '{,}');
    // Collapse number · π to adjacency: 2 \cdot \pi -> 2\pi
    tex = tex.replace(/(\d+(?:\{,\}\d+)?)\s*\\cdot\s*\\pi/g, '$1\\pi')
             .replace(/(\d+(?:\{,\}\d+)?)\s+\\pi/g, '$1\\pi')
             .replace(/(\d+(?:\{,\}\d+)?)\\,\\pi/g, '$1\\pi');
    // Display fractions and scalable parentheses
    tex = tex.replace(/\\frac/g, '\\dfrac')
             .replace(/(?<!\\left)\(/g, '\\left(')
             .replace(/(?<!\\right)\)/g, '\\right)');
    // Remove dot before parens/sqrt after single-letter, number, fraction, or command var
    tex = tex.replace(/(?<=^|[^A-Za-z])(\\?[A-Za-z])\s*\\cdot\s*\\left\(/g, '$1\\left(')
             .replace(/(?<=^|[^A-Za-z])(\\?[A-Za-z])\s*\\cdot\s*\(/g, '$1(')
             .replace(/(-?\d+(?:\{,\}\d+)?)\s*\\cdot\s*(\\left\(|\\sqrt)/g, '$1$2')
             .replace(/(\\dfrac\{[^}]+\}\{[^}]+\})\s*\\cdot\s*(\\left\(|\\sqrt)/g, '$1$2')
             .replace(/(\\[A-Za-z]+)\s*\\cdot\s*(\\sqrt|\\left\()/g, '$1$2');
    // Safety: normalize any remaining "+ -" or "- -" sequences in TeX
    tex = tex
      .replace(/\+\s*-\s*/g, ' - ')
      .replace(/-\s*-\s*/g, ' + ')
      .replace(/-\s*\+\s*/g, ' - ')
      .replace(/\+\s*\+\s*/g, ' + ');
    return tex;
  } catch {
    // Fallback to basic regex-based conversion
    const normalized = prepared.replace(/Math\.(sin|cos|tan|sqrt|abs|log|exp|pow|PI|E)/g, '$1');
    return normalized
      .replace(/sin\(([^)]+)\)/g, '\\sin($1)')
      .replace(/cos\(([^)]+)\)/g, '\\cos($1)')
      .replace(/tan\(([^)]+)\)/g, '\\tan($1)')
      .replace(/sqrt\(([^)]+)\)/g, '\\sqrt{$1}')
      .replace(/abs\(([^)]+)\)/g, '|$1|')
      .replace(/log\(([^)]+)\)/g, '\\ln($1)')
      .replace(/exp\(([^)]+)\)/g, 'e^{$1}')
      .replace(/pow\(([^,]+),\s*([^)]+)\)/g, '$1^{$2}')
      .replace(/PI|pi/g, '\\pi')
      .replace(/E/g, 'e')
      .replace(/\*\*/g, '^')
      .replace(/\*/g, ' \\cdot ')
      .replace(/\//g, '\\div ')
      .replace(/(\d)\s+\\cdot\s+\\pi/g, '$1\\pi')
      .replace(/\\pi\s+\\cdot\s+/g, '\\pi ')
      .replace(/\^\{([a-zA-Z0-9]+)\}/g, '^$1');
  }
};

// Human-readable formatting
export const formatExpressionForDisplay = (expr: string): string => {
  if (!expr) return '';
  const normalized = expr
    .replace(/Math\.(sin|cos|tan|sqrt|abs|log|exp|pow|PI|E)/g, '$1');
  return normalized
    .replace(/sin/g, 'sin')
    .replace(/cos/g, 'cos')
    .replace(/tan/g, 'tan')
    .replace(/sqrt/g, '√')
    .replace(/abs/g, '| |')
    .replace(/log/g, 'ln')
    .replace(/exp/g, 'e^')
    .replace(/pow\(([^,]+),\s*([^)]+)\)/g, '$1^$2')
    .replace(/PI/g, 'π')
    .replace(/E/g, 'e')
    .replace(/\*\*/g, '^')
    .replace(/\*/g, '×');
};

// LaTeX display with values
export const getFormulaLatexDisplay = (formula: Formula, x: number, y: number): string => {
  const formatNumber = (num: number): string => {
    const precision = Math.abs(num) >= 10 ? 2 : 4;
    return num.toFixed(precision).replace(/\.?0+$/, '');
  };

  const latexExpr = convertToLatex(formula.expression);
  return `${latexExpr.replace(/x/g, `(${formatNumber(x)})`)} = ${formatNumber(y)}`;
};
